import React, { useEffect, useState, useCallback, useMemo } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import { displayQueue, displayQueueStrategy, computeQueuePrefix } from './utils';
import './config-styles.css';

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

const STRATEGIES = [
  { value: 'ringall', label: 'Múltiplo - Toca em todos' },
  { value: 'leastrecent', label: 'Tempo - Menos recente' },
  { value: 'fewestcalls', label: 'Chamada - Menos chamadas' },
  { value: 'random', label: 'Aleatório - Toca aleatoriamente' },
  { value: 'rrmemory', label: 'Rotativo - Continua de onde parou' },
  { value: 'linear', label: 'Sequencial - Ordem fixa' },
  { value: 'wrandom', label: 'Carteira - Aleatório ponderado' },
  { value: 'rrordered', label: 'Rotativo - Ordem do cadastro' },
];

const ANNOUNCE_HOLDTIME_OPTIONS = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'once', label: 'Apenas uma vez' },
];
const ANNOUNCE_POSITION_OPTIONS = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'limit', label: 'Somente até o limite' },
  { value: 'more-than', label: 'Somente acima do limite' },
];

const DEFAULT_FORM = {
  queueKey: '',
  strategy: 'ringall',
  timeout: 30,
  wrapupTime: 10,
  maxlen: 0,
  servicelevel: 20,
  musiconhold: '',
  joinempty: '',
  leavewhenempty: '',
  weight: 0,
  ringinuse: 'no',
  autofill: 'yes',
  retry: '',
  announceFrequency: '',
  announceHoldtime: 'yes',
  announcePosition: 'yes',
  minAnnounceFrequency: '',
  ccv2MetricsEnabled: true,
};

const slugify = (value) => value.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const toNullableInt = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
};

function QueuesConfig() {
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [tenantPrefix, setTenantPrefix] = useState(null);
  const [mohClasses, setMohClasses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const fetchQueues = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      setTenantId(tid);
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/queues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tid,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setQueues(data.queues || data || []);
        setTenantPrefix(data?.namePrefix ?? null);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMohClasses = useCallback(async () => {
    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/api/config/moh/tenant/${encodeURIComponent(tid)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tid,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMohClasses(Array.isArray(data) ? data : []);
      }
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    fetchQueues();
    fetchMohClasses();
  }, [fetchQueues, fetchMohClasses]);

  const buildPayload = () => ({
    queueKey: formData.queueKey,
    strategy: formData.strategy,
    timeout: toNullableInt(formData.timeout),
    wrapupTime: toNullableInt(formData.wrapupTime),
    maxlen: toNullableInt(formData.maxlen),
    servicelevel: toNullableInt(formData.servicelevel),
    musiconhold: formData.musiconhold || undefined,
    joinempty: formData.joinempty || undefined,
    leavewhenempty: formData.leavewhenempty || undefined,
    weight: toNullableInt(formData.weight),
    ringinuse: formData.ringinuse,
    autofill: formData.autofill,
    retry: toNullableInt(formData.retry),
    announceFrequency: toNullableInt(formData.announceFrequency),
    announceHoldtime: formData.announceHoldtime,
    announcePosition: formData.announcePosition,
    minAnnounceFrequency: toNullableInt(formData.minAnnounceFrequency),
    ccv2MetricsEnabled: formData.ccv2MetricsEnabled !== false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const url = editingQueue
        ? `${apiBase}/v2/callcenter/queues/${encodeURIComponent(editingQueue.queueKey)}`
        : `${apiBase}/v2/callcenter/queues`;

      const method = editingQueue ? 'PUT' : 'POST';
      const payload = buildPayload();
      if (editingQueue) delete payload.queueKey;
      else payload.queueKey = slugify(payload.queueKey);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-tenant-id': tid,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingQueue(null);
        resetForm();
        fetchQueues();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao salvar fila: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao salvar fila:', e);
      alert('Erro ao salvar fila. Verifique sua conexão.');
    }
  };

  const handleDelete = async (queueKey) => {
    if (!window.confirm('Tem certeza que deseja excluir esta fila?')) return;

    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/queues/${encodeURIComponent(queueKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tid,
        },
      });

      if (response.ok) {
        fetchQueues();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao excluir fila: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao excluir fila:', e);
      alert('Erro ao excluir fila. Verifique sua conexão.');
    }
  };

  const openEditModal = (queue) => {
    setEditingQueue(queue);
    setFormData({
      queueKey: queue.queueKey || queue.name || '',
      strategy: queue.strategy || 'ringall',
      timeout: queue.timeout ?? 30,
      wrapupTime: queue.wrapupTime ?? 10,
      maxlen: queue.maxlen ?? 0,
      servicelevel: queue.servicelevel ?? 20,
      musiconhold: queue.musiconhold || '',
      joinempty: queue.joinempty || '',
      leavewhenempty: queue.leavewhenempty || '',
      weight: queue.weight ?? 0,
      ringinuse: queue.ringinuse || 'no',
      autofill: queue.autofill || 'yes',
      retry: queue.retry ?? '',
      ccv2MetricsEnabled: queue.ccv2MetricsEnabled !== false,
      announceFrequency: queue.announceFrequency ?? '',
      announceHoldtime: queue.announceHoldtime || 'yes',
      announcePosition: queue.announcePosition || 'yes',
      minAnnounceFrequency: queue.minAnnounceFrequency ?? '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
  };

  const openNewModal = () => {
    setEditingQueue(null);
    resetForm();
    setShowModal(true);
  };

  const effectivePrefix = useMemo(() => {
    if (tenantPrefix) return tenantPrefix;
    const names = queues.map((q) => q.name).filter(Boolean);
    return computeQueuePrefix(tenantId, names) || null;
  }, [tenantPrefix, queues, tenantId]);

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Configuração de Filas</h1>
          <p>Gerencie as filas do Call Center</p>
        </div>
        <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
          + Nova Fila
        </button>
      </div>

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando filas...</p>
        </div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Estratégia</th>
                  <th>Tempo de toque</th>
                  <th>Meta de SLA</th>
                  <th>Membros</th>
                  <th>No Call Center</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {queues.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="ccv2-empty-row">
                      Nenhuma fila cadastrada
                    </td>
                  </tr>
                ) : (
                  queues.map((queue) => (
                    <tr key={queue.queueKey}>
                      <td className="ccv2-cell-name" title={queue.name}>{displayQueue(queue.name, effectivePrefix)}</td>
                      <td>
                        <span className="ccv2-badge">{displayQueueStrategy(queue.strategy)}</span>
                      </td>
                      <td>{queue.timeout}s</td>
                      <td>{queue.servicelevel}s</td>
                      <td>{queue.memberCount}</td>
                      <td>
                        <span className={`ccv2-badge ${queue.ccv2MetricsEnabled === false ? '' : 'ccv2-badge--success'}`}>
                          {queue.ccv2MetricsEnabled === false ? 'Oculta' : 'Exibida'}
                        </span>
                      </td>
                      <td className="ccv2-cell-actions">
                        <button
                          className="ccv2-btn-icon ccv2-btn-edit"
                          onClick={() => openEditModal(queue)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="ccv2-btn-icon ccv2-btn-delete"
                          onClick={() => handleDelete(queue.queueKey)}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="ccv2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccv2-modal ccv2-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header">
              <h2>{editingQueue ? 'Editar Fila' : 'Nova Fila'}</h2>
              <button className="ccv2-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ccv2-modal-body">
                <div className="ccv2-form-group">
                  <label>Nome da Fila</label>
                  <input
                    type="text"
                    value={formData.queueKey}
                    onChange={(e) => setFormData({ ...formData, queueKey: e.target.value })}
                    placeholder="Ex: suporte-tecnico"
                    disabled={!!editingQueue}
                    required
                  />
                  {!editingQueue && (
                    <p className="ccv2-hint">Identificador salvo: {tenantId}-{slugify(formData.queueKey) || '...'}</p>
                  )}
                </div>

                <div className="ccv2-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.ccv2MetricsEnabled !== false}
                      onChange={(e) => setFormData({ ...formData, ccv2MetricsEnabled: e.target.checked })}
                    />
                    {' '}Exibir e contabilizar no Call Center
                  </label>
                  <p className="ccv2-hint">
                    Desmarcado, a fila some do Monitoramento e dos relatórios. As chamadas continuam
                    sendo gravadas no histórico — remarcar recupera o período.
                  </p>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Estratégia de Distribuição</label>
                    <select
                      value={formData.strategy}
                      onChange={(e) => setFormData({ ...formData, strategy: e.target.value })}
                    >
                      {STRATEGIES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ccv2-form-group">
                    <label>Música de Espera</label>
                    <select
                      value={formData.musiconhold}
                      onChange={(e) => setFormData({ ...formData, musiconhold: e.target.value })}
                    >
                      <option value="">Padrão do sistema</option>
                      {mohClasses.map((m) => (
                        <option key={m.name} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Tempo de toque por agente (segundos)</label>
                    <input
                      type="number"
                      value={formData.timeout}
                      onChange={(e) => setFormData({ ...formData, timeout: e.target.value })}
                      min="1"
                      max="300"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Tempo de finalização (segundos)</label>
                    <input
                      type="number"
                      value={formData.wrapupTime}
                      onChange={(e) => setFormData({ ...formData, wrapupTime: e.target.value })}
                      min="0"
                      max="300"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Meta de SLA (segundos)</label>
                    <input
                      type="number"
                      value={formData.servicelevel}
                      onChange={(e) => setFormData({ ...formData, servicelevel: e.target.value })}
                      min="1"
                      max="300"
                    />
                  </div>
                </div>

                <details className="ccv2-advanced-block">
                  <summary>Configurações avançadas</summary>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Tamanho Máx. da Fila (0 = sem limite)</label>
                    <input
                      type="number"
                      value={formData.maxlen}
                      onChange={(e) => setFormData({ ...formData, maxlen: e.target.value })}
                      min="0"
                      max="1000"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Peso da fila</label>
                    <input
                      type="number"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      min="0"
                      max="100"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Intervalo entre tentativas (segundos)</label>
                    <input
                      type="number"
                      value={formData.retry}
                      onChange={(e) => setFormData({ ...formData, retry: e.target.value })}
                      min="1"
                      max="300"
                    />
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Tocar em agente ocupado</label>
                    <select
                      value={formData.ringinuse}
                      onChange={(e) => setFormData({ ...formData, ringinuse: e.target.value })}
                    >
                      <option value="no">Não</option>
                      <option value="yes">Sim</option>
                    </select>
                  </div>

                  <div className="ccv2-form-group">
                    <label>Distribuição simultânea</label>
                    <select
                      value={formData.autofill}
                      onChange={(e) => setFormData({ ...formData, autofill: e.target.value })}
                    >
                      <option value="yes">Sim</option>
                      <option value="no">Não</option>
                    </select>
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Entrar em fila sem agente</label>
                    <input
                      type="text"
                      value={formData.joinempty}
                      onChange={(e) => setFormData({ ...formData, joinempty: e.target.value })}
                      placeholder="sim, nao ou lista (ex: paused,invalid)"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Sair quando a fila ficar sem agente</label>
                    <input
                      type="text"
                      value={formData.leavewhenempty}
                      onChange={(e) => setFormData({ ...formData, leavewhenempty: e.target.value })}
                      placeholder="sim, nao ou lista (ex: paused,invalid)"
                    />
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Anúncio de Posição</label>
                    <select
                      value={formData.announcePosition}
                      onChange={(e) => setFormData({ ...formData, announcePosition: e.target.value })}
                    >
                      {ANNOUNCE_POSITION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="ccv2-form-group">
                    <label>Anúncio de Tempo de Espera</label>
                    <select
                      value={formData.announceHoldtime}
                      onChange={(e) => setFormData({ ...formData, announceHoldtime: e.target.value })}
                    >
                      {ANNOUNCE_HOLDTIME_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Frequência de Anúncio (segundos)</label>
                    <input
                      type="number"
                      value={formData.announceFrequency}
                      onChange={(e) => setFormData({ ...formData, announceFrequency: e.target.value })}
                      min="0"
                      max="3600"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Frequência Mín. de Anúncio (segundos)</label>
                    <input
                      type="number"
                      value={formData.minAnnounceFrequency}
                      onChange={(e) => setFormData({ ...formData, minAnnounceFrequency: e.target.value })}
                      min="0"
                      max="3600"
                    />
                  </div>
                </div>
                </details>
              </div>

              <div className="ccv2-modal-footer">
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ccv2-btn ccv2-btn-primary">
                  {editingQueue ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueuesConfig;
